import { Link } from 'react-router-dom'
import { Plus } from '@phosphor-icons/react'
import { useI18n } from '@/i18n'
import { useActor } from '@/auth/pin/phoneSession'
import { hasAnyRole, ROLES } from '@/auth/roles'
import { Banner, BilingualLabel, Card, CardBody, CardHeader, EmptyState } from '@/ui'
import { isRunLive, type ProcessState } from '../projection'
import { sampleCandidates } from '../model/rules'
import { ProcessScreen } from '../components/ProcessScreen'
import { RunListItem } from '../components/RunCard'

/** Machine runs at this site: open ones first, then the recent closed ones with their result. */
export function RunsPage() {
  return <ProcessScreen title="process.runs.title">{(state) => <Runs state={state} />}</ProcessScreen>
}

function Runs({ state }: { state: ProcessState }) {
  const { t } = useI18n()
  const actor = useActor()
  const all = [...state.runs.values()].filter((r) => r.step_code !== 'DELABEL')
  const live = all.filter((r) => isRunLive(r.status))
  const done = all
    .filter((r) => !isRunLive(r.status))
    .sort((a, b) => (b.ended_at ?? b.opened_at ?? '').localeCompare(a.ended_at ?? a.opened_at ?? ''))
  const toSample = sampleCandidates(state)
  const flagged = all.filter((r) => r.status === 'EXCEPTION')
  const canOpen = hasAnyRole(actor.roles, [ROLES.MACHINE_OPERATOR, ROLES.SUPERVISOR])

  return (
    <div className="flex flex-col gap-4">
      {canOpen && (
        <Link
          to="/m/process/open"
          className="inline-flex min-h-touch-lg items-center justify-center gap-2 rounded-chip border border-ink bg-ink px-6 font-mono text-[14px] font-bold uppercase tracking-label text-paper"
        >
          <Plus aria-hidden="true" weight="bold" />
          <BilingualLabel k="process.open.title" />
        </Link>
      )}
      {toSample.length > 0 && (
        <Banner
          tone="info"
          action={
            <Link to="/m/process/qc-sample" className="font-mono text-[12px] font-bold uppercase underline">
              {t('process.qc.title')}
            </Link>
          }
        >
          {t('process.runs.sampleDue', { n: toSample.length })}
        </Banner>
      )}
      {flagged.length > 0 && hasAnyRole(actor.roles, [ROLES.SUPERVISOR]) && (
        <Banner
          tone="warn"
          action={
            <Link to="/m/process/investigate" className="font-mono text-[12px] font-bold uppercase underline">
              {t('process.investigate.title')}
            </Link>
          }
        >
          {t('process.runs.flagged', { n: flagged.length })}
        </Banner>
      )}
      <Card>
        <CardHeader eyebrow={t('process.runs.open', { n: live.length })} />
        <CardBody className="py-0">
          {live.length === 0 ? (
            <EmptyState title={t('process.runs.noneOpen')} className="py-6" />
          ) : (
            <ul>
              {live.map((r) => (
                <RunListItem key={r.id} state={state} run={r} />
              ))}
            </ul>
          )}
        </CardBody>
      </Card>
      {done.length > 0 && (
        <Card>
          <CardHeader eyebrow={t('process.runs.recent')} />
          <CardBody className="py-0">
            <ul>
              {done.slice(0, 12).map((r) => (
                <RunListItem key={r.id} state={state} run={r} />
              ))}
            </ul>
          </CardBody>
        </Card>
      )}
    </div>
  )
}
